import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Search,
  Eye,
  Edit,
  Copy,
  Trash2,
  MoreVertical,
  FileText,
  ArrowLeft,
  CalendarIcon,
  X,
  User,
  Download
} from "lucide-react";
import * as XLSX from "xlsx";

const QuoteHistory = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  useEffect(() => {
    // Check for URL params (from dashboard clicks)
    const status = searchParams.get("status");
    const category = searchParams.get("category");
    const currency = searchParams.get("currency");
    if (status) setStatusFilter(status);
    if (category) setCategoryFilter(category);
    if (currency) setCurrencyFilter(currency);
    
    fetchQuotes();
    fetchUsers();
  }, [searchParams]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(res.data);
    } catch (error) {
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      // User might not have admin access, ignore
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cotización duplicada");
      navigate(`/editar/${res.data.id}`);
    } catch (error) {
      toast.error("Error al duplicar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta cotización?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cotización eliminada");
      setQuotes(quotes.filter(q => q.id !== id));
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: "Borrador", className: "status-draft" },
      sent: { label: "Enviada", className: "status-sent" },
      accepted: { label: "Aceptada", className: "status-accepted" },
      rejected: { label: "Rechazada", className: "status-rejected" }
    };
    const c = config[status] || config.draft;
    return <span className={`status-badge ${c.className}`}>{c.label}</span>;
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCategoryFilter("all");
    setCurrencyFilter("all");
    setUserFilter("all");
    setDateFrom(null);
    setDateTo(null);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || quote.status === statusFilter;
    const matchesCategory = categoryFilter === "all" || quote.category === categoryFilter;
    const matchesCurrency = currencyFilter === "all" || quote.currency === currencyFilter;
    const matchesUser = userFilter === "all" || quote.created_by_id === userFilter || quote.user_id === userFilter;
    
    // Date range filter
    let matchesDateRange = true;
    if (dateFrom || dateTo) {
      const quoteDate = new Date(quote.created_at);
      if (dateFrom && quoteDate < dateFrom) matchesDateRange = false;
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        if (quoteDate > endOfDay) matchesDateRange = false;
      }
    }
    
    return matchesSearch && matchesStatus && matchesCategory && matchesCurrency && matchesUser && matchesDateRange;
  });

  const hasActiveFilters = searchTerm || statusFilter !== "all" || categoryFilter !== "all" || currencyFilter !== "all" || userFilter !== "all" || dateFrom || dateTo;

  const statusLabels = {
    draft: "Borrador",
    sent: "Enviada",
    accepted: "Aceptada",
    rejected: "Rechazada"
  };

  const exportToExcel = (type = "current") => {
    if (filteredQuotes.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    let data;
    
    if (type === "current") {
      // Vista Actual: Solo las columnas mostradas en la tabla
      data = filteredQuotes.map(quote => ({
        "Fecha de Cotización": quote.created_at ? new Date(quote.created_at).toLocaleDateString("es-MX") : "",
        "Número": quote.quote_number || "",
        "Cliente": quote.client_company || quote.client_name || "",
        "Unidad de Negocio": quote.business_unit || "",
        "Proyecto": quote.project_name || "",
        "Destino": quote.destination || "",
        "Total": quote.total || 0,
        "Moneda": quote.currency || "USD",
        "Estado": statusLabels[quote.status] || quote.status,
        "Realizada por": quote.created_by_name || ""
      }));
    } else {
      // Vista Completa: Todos los campos del registro
      data = filteredQuotes.map(quote => ({
        "ID": quote.id || "",
        "Número de Cotización": quote.quote_number || "",
        "Nombre del Proyecto": quote.project_name || "",
        "Unidad de Negocio": quote.business_unit || "",
        "Empresa Cliente": quote.client_company || "",
        "Nombre del Cliente": quote.client_name || "",
        "Email Cliente": quote.client_email || "",
        "Teléfono Cliente": quote.client_phone || "",
        "Tipo de Cotización": quote.quote_type || "",
        "Categoría": quote.category || "",
        "Destino": quote.destination || "",
        "Origen": quote.origin || "",
        "Fecha Inicio": quote.start_date || "",
        "Fecha Fin": quote.end_date || "",
        "Adultos": quote.passengers?.adults || 0,
        "Menores": quote.passengers?.minors || 0,
        "Infantes": quote.passengers?.infants || 0,
        "Moneda": quote.currency || "USD",
        "Subtotal": quote.subtotal || 0,
        "Costo Total": quote.total_cost || 0,
        "IVA %": quote.iva_percentage || 0,
        "IVA Monto": quote.iva_amount || 0,
        "Total": quote.total || 0,
        "Utilidad": quote.profit || 0,
        "Días de Vigencia": quote.validity_days || 15,
        "Estado": statusLabels[quote.status] || quote.status,
        "Notas": quote.notes || "",
        "Términos": quote.terms || "",
        "Creado por (ID)": quote.user_id || "",
        "Creado por (Nombre)": quote.created_by_name || quote.user_name || "",
        "Creado por (Email)": quote.user_email || "",
        "Asignado a (ID)": quote.assigned_user_id || "",
        "Asignado a (Nombre)": quote.assigned_user_name || "",
        "Fecha de Creación": quote.created_at ? new Date(quote.created_at).toLocaleString("es-MX") : "",
        "Fecha de Actualización": quote.updated_at ? new Date(quote.updated_at).toLocaleString("es-MX") : ""
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cotizaciones");
    
    // Generate filename with date and type
    const today = new Date().toISOString().split("T")[0];
    const suffix = type === "full" ? "_completo" : "_vista";
    XLSX.writeFile(workbook, `cotizaciones${suffix}_${today}.xlsx`);
    
    const columnCount = type === "full" ? "todos los campos" : "campos de la vista";
    toast.success(`Excel exportado con ${columnCount}`);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando cotizaciones...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="quote-history-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Cotizaciones Realizadas</h1>
              <p className="text-slate-500 text-sm">{filteredQuotes.length} de {quotes.length} cotizaciones</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={filteredQuotes.length === 0}
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportToExcel("current")}>
                  <FileText className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span>Vista Actual</span>
                    <span className="text-xs text-slate-500">Solo columnas del panel</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToExcel("full")}>
                  <Download className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span>Vista Completa</span>
                    <span className="text-xs text-slate-500">Todos los campos del registro</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => navigate("/nueva-cotizacion")}>
              Nueva Cotización
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="sent">Enviada</SelectItem>
                  <SelectItem value="accepted">Aceptada</SelectItem>
                  <SelectItem value="rejected">Rechazada</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  <SelectItem value="MICE">MICE</SelectItem>
                  <SelectItem value="LEISURE">Leisure</SelectItem>
                </SelectContent>
              </Select>

              {/* Currency Filter */}
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Moneda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las monedas</SelectItem>
                  <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                  <SelectItem value="USD">USD - Dólar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                </SelectContent>
              </Select>

              {/* User Filter */}
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Realizada por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters} className="flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Limpiar
                </Button>
              )}
            </div>

            {/* Date Range */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500">Desde:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-[150px] justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "dd/MM/yyyy") : "Fecha inicio"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} locale={es} />
                  </PopoverContent>
                </Popover>
                {dateFrom && (
                  <Button variant="ghost" size="sm" onClick={() => setDateFrom(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500">Hasta:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-[150px] justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "dd/MM/yyyy") : "Fecha fin"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={dateTo} onSelect={setDateTo} locale={es} />
                  </PopoverContent>
                </Popover>
                {dateTo && (
                  <Button variant="ghost" size="sm" onClick={() => setDateTo(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No se encontraron cotizaciones</p>
                {hasActiveFilters && (
                  <Button variant="link" onClick={clearFilters}>
                    Limpiar filtros
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha de Cotización</TableHead>
                      <TableHead>Número</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Unidad de Negocio</TableHead>
                      <TableHead>Proyecto</TableHead>
                      <TableHead>Destino</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Moneda</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Realizada por</TableHead>
                      <TableHead className="w-[80px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotes.map((quote) => (
                      <TableRow 
                        key={quote.id} 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/cotizacion/${quote.id}`)}
                      >
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(quote.created_at)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                        <TableCell className="font-medium">{quote.client_company || quote.client_name || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{quote.business_unit || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{quote.project_name || "—"}</TableCell>
                        <TableCell>{quote.destination}</TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(quote.total, quote.currency)}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            quote.currency === "MXN" ? "bg-green-100 text-green-700" :
                            quote.currency === "USD" ? "bg-blue-100 text-blue-700" :
                            quote.currency === "EUR" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>
                            {quote.currency || "USD"}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(quote.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-slate-400" />
                            <span className="text-sm">{quote.created_by_name || "—"}</span>
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/cotizacion/${quote.id}`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver
                              </DropdownMenuItem>
                              {quote.can_edit !== false && (
                                <DropdownMenuItem onClick={() => navigate(`/editar/${quote.id}`)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDuplicate(quote.id)}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicar
                              </DropdownMenuItem>
                              {user?.role === "admin" && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(quote.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuoteHistory;
